import type { AxiosInstance } from "axios";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { useState } from "react";

import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { apiErrorMessage } from "../lib/api.js";
import { Logo } from "../components/Logo.js";

/**
 * "I forgot my password."
 *
 * THE RESPONSE IS THE SAME WHETHER OR NOT THE ADDRESS EXISTS. The API answers 200 to every
 * call (enumeration defence — /auth/password/forgot), so this screen shows the same "check
 * your email" either way. Telling the caller "no account with that email" would turn the
 * form into an account checker that needs no password.
 *
 * The link in the email lands on /set-password, which is the SAME redemption screen an
 * invite uses — this screen only asks for the address and sends the mail.
 */
export function ForgotPasswordScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.post("/auth/password/forgot", { email: email.trim() });
      setSent(true);
    } catch (caught) {
      // A network failure is the only thing that lands here — the server answers 200 for
      // every real outcome, so there is nothing account-specific to reveal.
      setError(apiErrorMessage(caught, "Could not send the reset email. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-(--page) px-4 py-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-amber-400 opacity-20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-blue-500 opacity-20 blur-3xl" />
      </div>

      <div className="absolute top-8 left-8 z-10">
        <Logo className="h-7 w-auto" />
      </div>

      <div className="bg-card relative w-full max-w-sm rounded-2xl border p-8 shadow-xl">
        {sent ? (
          <div className="flex flex-col items-center gap-6 text-center">
            <span className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-full">
              <MailCheck className="size-8" />
            </span>
            <div>
              <h1 className="text-2xl font-bold">Check your email</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                If an account exists for <span className="text-foreground font-medium">{email}</span>, we sent a link to
                reset its password. The link expires soon — use it from the same device if you can.
              </p>
            </div>
            <Button asChild variant="outline" className="h-11 w-full">
              <a href="/">Back to sign in</a>
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold">Reset your password</h1>
              <p className="text-muted-foreground mt-2 text-sm">Enter your email and we&apos;ll send you a reset link.</p>
            </div>

            <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
              <div className="grid gap-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@company.com"
                  required
                  className="h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {error !== null && (
                <p className="text-destructive text-sm" role="alert">
                  {error}
                </p>
              )}

              <Button type="submit" disabled={busy} className="h-11 w-full">
                {busy && <Loader2 className="animate-spin" />}
                {busy ? "Sending…" : "Send reset link"}
              </Button>

              <a href="/" className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 text-sm">
                <ArrowLeft className="size-4" />
                Back to sign in
              </a>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
